/**
 * Dynamic forms edit for forms created by users. See also './Dynamic.js'.
 * Currently only used by the coporate onboarding form, so may not yet be ready
 * for use in other pages. (EXPERIMENTAL)
 */

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  SpeedDialIcon,
  TextField,
  Typography,
} from '@mui/material';

import {
  Add,
  Title,
  Close,
  ArrowDropUp,
  ArrowDropDown,
  AssignmentTurnedIn,
  Assignment,
} from '@mui/icons-material';

import { useState, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'src/components/form/Context';
import { Trans, useTranslation } from 'react-i18next';

export function DynamicFormEditor({ id, defaultValue = [] }) {
  const { t } = useTranslation();
  const { value: fields, setValue: setFields } = useForm(id, defaultValue);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => setAnchorEl(null);

  const addField = (type) => {
    const defaultValue = {
      text: Text.defaultValue,
      'multiple-choice': MultipleChoice.defaultValue,
      'multi-select': MultiSelect.defaultValue,
    }[type];
    const newField = { id: uuidv4(), type, value: { ...defaultValue } };
    setFields((fields) => [...fields, newField]);
  };

  if (!fields) {
    return null;
  }

  return (
    <>
      <ExamPage fields={fields} setFields={setFields} />

      <Box display="flex" alignItems="center" justifyContent="Center">
        <Button
          startIcon={<SpeedDialIcon />}
          color="primary"
          variant="contained"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {t('form.dynamic.add-question')}
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom' }}
      >
        {[
          {
            id: 'text',
            name: t('form.dynamic.type-text'),
            icon: <Title />,
          },
          {
            id: 'multiple-choice',
            name: t('form.dynamic.type-multiple-choice'),
            icon: <Assignment />,
          },
          {
            id: 'multi-select',
            name: t('form.dynamic.type-multi-select'),
            icon: <AssignmentTurnedIn />,
          },
        ].map((el) => (
          <MenuItem
            key={el.id}
            onClick={() => {
              addField(el.id);
              handleClose();
            }}
          >
            <ListItemIcon>{el.icon}</ListItemIcon>
            <ListItemText>{el.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ExamPage({ fields, setFields }) {
  const updateField = (i) => (updates) => {
    setFields((fields) =>
      fields.map((field, j) =>
        i === j ? { ...field, value: updates(field.value) } : field
      )
    );
  };

  const removeField = (id) => () => {
    setFields((fields) => fields.filter((_field, i) => i !== id));
  };

  const moveField = (from, to) => {
    setFields((fields) => {
      const item = fields[from];
      fields.splice(from, 1);
      fields.splice(to, 0, item);
      return fields;
    });
  };

  const renderField = (field, i) => (
    <FieldContainer
      key={i}
      field={field}
      index={i}
      moveField={moveField}
      removeField={removeField(i)}
    >
      {(() => {
        switch (field.type) {
          case 'text':
            return <Text value={field.value} setValue={updateField(i)} />;
          case 'multiple-choice':
            return (
              <MultipleChoice value={field.value} setValue={updateField(i)} />
            );
          case 'multi-select':
            return (
              <MultiSelect value={field.value} setValue={updateField(i)} />
            );
          default:
            console.log(field.type);
            throw new Error('Unreachable');
        }
      })()}
    </FieldContainer>
  );

  return fields.length > 0 ? (
    fields.map(renderField)
  ) : (
    <Box
      width="100%"
      height="calc(100vh - 335px)"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Typography
        color="text.secondary"
        sx={{ display: 'flex', alignItems: 'center', gap: '0.25em' }}
      >
        <Trans
          i18nKey="form.dynamic.press-to-add-question"
          components={{ icon: <Add /> }}
        />
      </Typography>
    </Box>
  );
}

const FieldContainer = ({ field, index, removeField, moveField, children }) => {
  const ref = useRef();

  const handleMove = (from, to) => {
    moveField(from, to);
  };

  const handleMoveUp = () => handleMove(index, index - 1);
  const handleMoveDown = () => handleMove(index, index + 1);

  return (
    <Card ref={ref} sx={{ mb: 4 }}>
      <CardContent>
        <Box display="flex" alignItems="center" minHeight="48px">
          <Box
            width="30px"
            marginRight="20px"
            height="50px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton size="small" onClick={handleMoveUp}>
              <ArrowDropUp sx={{ width: 16, height: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => removeField(field.id)}>
              <Close sx={{ width: 16, height: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={handleMoveDown}>
              <ArrowDropDown sx={{ width: 16, height: 16 }} />
            </IconButton>
          </Box>
          <Box flex="1">{children}</Box>
        </Box>
      </CardContent>
    </Card>
  );
};

Text.defaultValue = { text: '' };
function Text({ value, setValue }) {
  const { t } = useTranslation();
  const handleUpdateTitle = (e) => {
    setValue((value) => ({ ...value, title: e.target.value }));
  };

  return (
    (value ?? null) && (
      <Box display="flex" flexDirection="column">
        <TextField
          label={t('form.dynamic.question')}
          margin="normal"
          variant="standard"
          value={value.title}
          onChange={handleUpdateTitle}
        />
        <FormControl>
          <TextField
            label={t('form.dynamic.answer')}
            margin="normal"
            variant="filled"
            multiline
            maxRows={3}
            disabled
          />
        </FormControl>
      </Box>
    )
  );
}

MultipleChoice.defaultValue = {
  title: '',
  options: [...new Array(4).keys()].map((i) => `Option ${i + 1}`),
};
function MultipleChoice({ value, setValue }) {
  const { t } = useTranslation();
  const handleUpdateTitle = (e) => {
    setValue((value) => ({ ...value, title: e.target.value }));
  };

  const handleUpdateOption = (id) => (e) => {
    setValue((value) => ({
      ...value,
      options: value.options.map((option, i) =>
        id === i ? e.target.value : option
      ),
    }));
  };

  const handleRemoveOption = (id) => () => {
    setValue((value) => ({
      ...value,
      options: value.options.filter((_, i) => i !== id),
    }));
  };

  const handleAddOption = () => {
    setValue((value) => ({
      ...value,
      options: [...value.options, `Option ${value.options.length + 1}`],
    }));
  };

  const options = useMemo(() => {
    const options = [];
    if (!value) return [];
    for (let i = 0, e = value.options.length; i < e; i++) {
      const item = value.options[i];

      options.push(
        <FormControlLabel
          key={i}
          value={i}
          disabled
          control={<Radio />}
          label={
            <Box display="flex" alignItems="center">
              <TextField
                sx={{ width: '40vw' }}
                variant="standard"
                value={item}
                onChange={handleUpdateOption(i)}
              />
              <IconButton
                disabled={i === 0}
                size="small"
                onClick={handleRemoveOption(i)}
              >
                <Close
                  sx={{
                    width: 16,
                    height: 16,
                    display: i === 0 ? 'none' : 'block',
                  }}
                />
              </IconButton>
            </Box>
          }
        />
      );
    }

    return options;
  }, [value?.options]);

  return value ? (
    <Box display="flex" flexDirection="column">
      <TextField
        label={t('form.dynamic.question')}
        margin="normal"
        variant="standard"
        value={value.title}
        onChange={handleUpdateTitle}
      />
      <FormControl>
        <RadioGroup value={false} onChange={() => {}}>
          {options}
        </RadioGroup>
      </FormControl>
      <Button
        startIcon={<Add />}
        onClick={handleAddOption}
        variant="outlined"
        sx={{ maxWidth: '225px', marginLeft: '15px', marginTop: '15px' }}
      >
        {t('form.dynamic.add-option')}
      </Button>
    </Box>
  ) : (
    <></>
  );
}

MultiSelect.defaultValue = {
  title: '',
  options: [...new Array(4).keys()].map((i) => `Option ${i + 1}`),
};
function MultiSelect({ value, setValue }) {
  const { t } = useTranslation();
  const handleUpdateTitle = (e) => {
    setValue((value) => ({ ...value, title: e.target.value }));
  };

  const handleUpdateOption = (id) => (e) => {
    setValue((value) => ({
      ...value,
      options: value.options.map((option, i) =>
        id === i ? e.target.value : option
      ),
    }));
  };

  const handleRemoveOption = (id) => () => {
    setValue((value) => ({
      ...value,
      options: value.options.filter((_, i) => i !== id),
    }));
  };

  const handleAddOption = () => {
    const options = value.options;
    setValue((value) => ({
      ...value,
      options: [...options, `Option ${options.length + 1}`],
    }));
  };

  const options = useMemo(() => {
    const options = [];
    if (!value) return [];
    for (let i = 0, e = value.options.length; i < e; i++) {
      const item = value.options[i];

      options.push(
        <FormControlLabel
          key={i}
          control={<Checkbox checked={false} onChange={() => {}} disabled />}
          label={
            <Box display="flex" alignItems="center">
              <TextField
                sx={{ width: '40vw' }}
                variant="standard"
                value={item}
                onChange={handleUpdateOption(i)}
              />
              <IconButton
                disabled={i === 0}
                size="small"
                onClick={handleRemoveOption(i)}
              >
                <Close
                  sx={{
                    width: 16,
                    height: 16,
                    display: i === 0 ? 'none' : 'block',
                  }}
                />
              </IconButton>
            </Box>
          }
        />
      );
    }

    return options;
  }, [value?.options]);

  return value ? (
    <Box display="flex" flexDirection="column">
      <TextField
        label={t('form.dynamic.question')}
        margin="normal"
        variant="standard"
        value={value.title}
        onChange={handleUpdateTitle}
      />
      <FormControl>
        <RadioGroup value={false} onChange={() => {}}>
          {options}
        </RadioGroup>
      </FormControl>
      <Button
        startIcon={<Add />}
        onClick={handleAddOption}
        variant="outlined"
        sx={{ maxWidth: '225px', marginLeft: '15px', marginTop: '15px' }}
      >
        {t('form.dynamic.add-option')}
      </Button>
    </Box>
  ) : (
    <></>
  );
}
